/* eslint-disable no-underscore-dangle */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { setCurrentUser, setUserToken, setLocalStorage } from '../common/util';
import { signinAPI, userVerifyAPI, signoutAPI } from '../api/auth';
import { managerInAPI } from '../api/privateManager';

const initialUser = {
  userId: '',
  nickName: '',
  institution: '',
  url: '',
  userPId: '',
  role: '',
};
const initialState = {
  pending: false,
  checking: { id: false, mail: false, auth: false, send: false },
  checked: { id: null, mail: null, auth: null, send: null },
  isLoggedIn: null,
  user: { ...initialUser },
  error: null,
  authError: null,
  mode: 0,
  bManage: false,
};

const userInfo = JSON.parse(localStorage.getItem('currentUser'));

export const managerIn = createAsyncThunk(
  `manager/MANAGER_IN`,
  async ({ userId, serial }, { rejectWithValue }) => {
    try {
      const { result, robot, userPId, role } = await managerInAPI({
        userId,
        serial,
      });
      if (result) {
        const nickName = `${serial} 로봇 매니저`;
        setLocalStorage('currentUser', {
          nickName,
          robots: [robot],
          userId,
          userPId,
        });
        setLocalStorage('robotId', robot.robotId);
        setLocalStorage('robotPId', robot._id);
        setLocalStorage('manager', true);
        return {
          user: {
            nickName,
            institution: '',
            url: '/image/img_user.png',
            userPId,
            role,
          },
          isLoggedIn: true,
        };
      }
      return rejectWithValue('입력 정보로 로그인 할 수 없습니다.');
    } catch (error) {
      return rejectWithValue('로그인 중 오류가 발생 했습니다.');
    }
  },
);

export const signIn = createAsyncThunk(
  'manager/SIGN_IN',
  async ({ userId, password }, { rejectWithValue }) => {
    try {
      const data = await signinAPI(userId, password);
      const {
        result,
        token,
        data: {
          userId: responseUserId,
          userPId,
          nickName,
          institution,
          robots,
          role,
        },
      } = data;
      if (result) {
        await setCurrentUser({
          userPId,
          responseUserId,
          robots,
          nickName,
          institution,
          role,
        });
        await setUserToken(token);
        return {
          user: {
            nickName,
            institution,
            url: '/image/img_user.png',
            userPId,
            role,
          },
          isLoggedIn: true,
        };
      }
      return rejectWithValue(false);
    } catch (err) {
      const { error } = await err.json();
      if ('code' in error && 'desc' in error) {
        const { code, desc } = error;
        if (code === '4u105b') {
          return rejectWithValue(
            '계정 정보가 존재하지 않습니다. 회원가입을 하고 이용하세요.',
          );
        }
        if (code === '4u105c') {
          return rejectWithValue('아이디 또는 비밀번호가 일치하지 않습니다.');
        }
        if (code === '4u105d') {
          return rejectWithValue(
            '이메일 인증이 필요한 계정입니다. 이메일 인증을 다시 진행해주세요.',
          );
        }
        return rejectWithValue(desc);
      }
      return rejectWithValue(
        '알 수 없는 오류가 발생했습니다. 관리자에게 문의하세요.',
      );
    }
  },
);

export const signOut = createAsyncThunk('manager/SIGN_OUT', async () => {
  localStorage.removeItem('currentUser');
  localStorage.removeItem('userToken');
  if (userInfo) await signoutAPI({ userPId: userInfo.userPId });
  // history.replace('/signin');
  return {
    user: initialUser,
    isLoggedIn: false,
  };
});

export const checkAuth = createAsyncThunk(
  'manager/CHECK_AUTH',
  async (token, { rejectWithValue }) => {
    try {
      if (token) {
        const res = await userVerifyAPI(token);
        const { result, info, expired, error } = res;
        if (result) {
          const { userId, userPId, robots, nickName, institution, role } = info;
          await setCurrentUser({
            userPId,
            userId,
            robots,
            nickName,
            institution,
            role,
          });
          return { user: info, isLoggedIn: true };
        }
        if (expired) {
          throw error;
        }
        return rejectWithValue(
          '알 수 없는 오류가 발생했습니다. 관리자에게 문의하세요.',
        );
      }
      return rejectWithValue('인증 토큰이 존재하지 않습니다.');
    } catch (err) {
      const { error } = await err.json();
      if ('code' in error && 'desc' in error) {
        const { code, desc } = error;
        if (['4u113a', '4u113b', '4u114a', '4u114b'].indexOf(code) > -1) {
          return rejectWithValue(desc);
        }
      }
      return rejectWithValue(
        '알 수 없는 오류가 발생했습니다. 관리자에게 문의하세요.',
      );
    }
  },
);

const managerSlice = createSlice({
  name: 'manager',
  initialState,
  reducers: {
    setMode: (state, action) => ({ ...state, bManage: action.payload }),
  },
  extraReducers: {
    [managerIn.fulfilled.type]: (state, action) => ({
      ...state,
      ...action.payload,
    }),
    [managerIn.rejected.type]: (state, action) => ({
      ...state,
      error: {
        ...state.error,
        login: action.payload || '로그인에 실패하였습니다.',
      },
    }),
    [signIn.pending.type]: (state) => ({
      ...state,
      pending: true,
      error: null,
    }),
    [signIn.fulfilled.type]: (state, action) => ({
      ...state,
      pending: false,
      ...action.payload,
    }),
    [signIn.rejected.type]: (state, action) => ({
      ...initialState,
      error: {
        ...state.error,
        login: action.payload || '로그인에 실패하였습니다.',
      },
    }),
    [signOut.pending.type]: (state) => ({ ...state, pending: true }),
    [signOut.fulfilled.type]: (state, action) => ({
      ...state,
      pending: false,
      ...action.payload,
    }),
    [checkAuth.pending.type]: (state) => ({
      ...state,
      pending: true,
      authError: null,
    }),
    [checkAuth.fulfilled.type]: (state, action) => ({
      ...state,
      pending: false,
      ...action.payload,
    }),
    [checkAuth.rejected.type]: (state, action) => ({
      ...state,
      pending: false,
      authError: action.payload,
    }),
  },
});

const { reducer: ManagerReducer, actions } = managerSlice;
export const { setMode } = actions;

export default ManagerReducer;
